/*@flow*/
import * as t from 'lib/t';
import type { ValidationTypeT } from 'lib/t/types';

export type TemplateProps = {
  hasError: boolean,
  help: React$Node,
  error: React$Node,
  items: React$Node[],
  label: string | React$Node,
  value: any,
  path: string[],
  onChange: Function,
};

// Helper functions
const re = /^([A-Za-z0-9_\-.])+@([A-Za-z0-9_\-.])+\.([A-Za-z]{2,4})$/;
const isEmail = x => re.test(x);
const isPassword = x => x && x.length >= 8;
const identity = () => true;

// Types
export const EmailT: ValidationTypeT = (() => {
  const ret = (t.subtype(t.String, isEmail): any);
  ret.getValidationErrorMessage = (
    value: any /* , path: string[] | void , ctx: Object*/
  ): any => {
    if (!value) {
      return 'Email is required';
    }
    if (!isEmail(value)) {
      return 'Not a valid email address.';
    }
  };
  return ret;
})();

export const PasswordT: ValidationTypeT = (() => {
  const ret = (t.subtype(t.String, isPassword): any);
  ret.getValidationErrorMessage = (
    value: any /* , path: string[] | void , ctx: Object*/
  ): any => {
    if (!value) {
      return 'Password is required';
    }
    if (!isPassword(value)) {
      return 'Not a valid password.';
    }
  };
  return ret.opt({ type: 'password' });
})();

export const PasswordConfirmationT: ValidationTypeT = (() => {
  const ret = (t.subtype(PasswordT, identity): any);
  ret.getValidationErrorMessage = (
    value: any /* , path: string[] | void , ctx: Object*/
  ): any => {
    if (!value) {
      return 'Password confirmation is required';
    }
    if (!isPassword(value)) {
      return 'Not a valid password.';
    }
  };
  return ret.opt({ type: 'password' });
})();

export const NonEmptyString = t.subtype(
  t.String,
  x => x && x !== '',
  'NonEmptyString'
);

export const EmptyString = t.subtype(t.String, x => x === '', 'EmptyString');

export const NonEmptyArray = t.subtype(
  t.Array,
  x => x && x.length !== 0,
  'NonEmptyArray'
);

export const EmptyArray = t.subtype(
  t.Array,
  x => x && x.length === 0,
  'EmptyArray'
);

export const Present = t.subtype(
  t.Any,
  x => (t.Array.is(x) ? NonEmptyArray.is(x) : Boolean(x)),
  'Present'
);

export const Blank = t.subtype(
  t.Any,
  x => (t.Array.is(x) ? EmptyArray.is(x) : !x),
  'Blank'
);

let getDigitsFromValue = (v = '') =>
  v && typeof v === 'string'
    ? parseInt(v.replace(/(-(?!\d))|[^0-9|-]/g, ''), 10)
    : 0;

export const NormalizedNumber = t.irreducible('NormalizedNumber', value => {
  return t.Number.meta.predicate(getDigitsFromValue(value));
});

export const Id = t.irreducible('Id', v => t.String.is(v) || t.Number.is(v));
